// HIG8 (GOALS/GOALS-DESIGN.html#hig8): the Mac menu bar carries the app's own
// commands. The Electron main process sends each chosen item to the focused
// window's renderer as one "desktop:menu-action" message carrying a command
// id (apps/desktop/src/window/DesktopApplicationMenu.ts); the preload exposes
// that stream as desktopBridge.onMenuAction. This module is the renderer side:
// it subscribes when the bridge is present and is a no-op on the web.
import { useEffect, useRef } from "react";

export const DESKTOP_MENU_COMMAND_IDS = [
  "new-room",
  "new-thread",
  "go-to-file",
  "search-commands",
  "toggle-room-rail",
  "toggle-context-rail",
  "toggle-cleo-wide",
] as const;

export type DesktopMenuCommandId = (typeof DESKTOP_MENU_COMMAND_IDS)[number];

export type DesktopMenuCommandHandlers = Partial<Record<DesktopMenuCommandId, () => void>>;

export function isDesktopMenuCommandId(action: string): action is DesktopMenuCommandId {
  return (DESKTOP_MENU_COMMAND_IDS as readonly string[]).includes(action);
}

type MenuActionBridge = {
  readonly onMenuAction?: (listener: (action: string) => void) => () => void;
};

function resolveMenuActionBridge(): MenuActionBridge | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as { desktopBridge?: MenuActionBridge }).desktopBridge;
}

// Subscribes `handlers` to the desktop menu stream. Actions that are not one
// of DESKTOP_MENU_COMMAND_IDS (open-settings, the account actions) belong to
// other subscribers and are ignored here. Returns the unsubscribe; a no-op
// function when no desktop bridge is present.
export function subscribeDesktopMenuCommands(
  handlers: DesktopMenuCommandHandlers,
  bridge: MenuActionBridge | undefined = resolveMenuActionBridge(),
): () => void {
  const onMenuAction = bridge?.onMenuAction;
  if (typeof onMenuAction !== "function") return () => {};
  return onMenuAction((action) => {
    if (!isDesktopMenuCommandId(action)) return;
    handlers[action]?.();
  });
}

// React hook for the root route: subscribe once per mount, always dispatch to
// the latest handlers (so callers may pass fresh closures every render).
export function useDesktopMenuCommands(handlers: DesktopMenuCommandHandlers): void {
  const latest = useRef(handlers);
  latest.current = handlers;
  useEffect(() => {
    const forwarding = Object.fromEntries(
      DESKTOP_MENU_COMMAND_IDS.map((id) => [id, () => latest.current[id]?.()]),
    ) as DesktopMenuCommandHandlers;
    return subscribeDesktopMenuCommands(forwarding);
  }, []);
}

// HIG8: the window title names the World and the room instead of the
// wordmark. The renderer publishes it through document.title; the desktop
// main process applies it to the native window on page-title-updated and
// falls back to the branded name when the title is only the wordmark.
export const WINDOW_TITLE_SEPARATOR = " · ";

export function formatDesktopWindowTitle(input: {
  readonly world?: string | null;
  readonly room?: string | null;
}): string {
  const parts = [input.world, input.room]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part.length > 0);
  return parts.join(WINDOW_TITLE_SEPARATOR);
}

export function useDesktopWindowTitle(input: {
  readonly world?: string | null;
  readonly room?: string | null;
}): void {
  const title = formatDesktopWindowTitle(input);
  useEffect(() => {
    if (resolveMenuActionBridge() === undefined) return;
    if (typeof document === "undefined") return;
    const previous = document.title;
    if (title.length === 0) return;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}
